import { LoaderCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate, useLocation, useMatches, useSearchParams } from 'react-router';
import { PageContainer } from '@/components/page';
import { useLoadError } from '@/components/use-load-error';
import { isApiError } from '@/lib/api/client';
import { useSession } from '@/lib/api/queries';
import { safeNextPath } from '@/lib/hooks';
import { NotFoundPage } from '@/pages/not-found-page';
import { LoadFailedNotice } from '@/pages/refresh-notice';
import { StandaloneLayout } from '@/pages/standalone-layout';

export function FullPageLoader({ label = 'Loading ComfiConf…' }: { label?: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center" role="status">
      <LoaderCircle className="size-6 animate-spin text-accent" aria-hidden />
      <span className="sr-only">{label}</span>
    </div>
  );
}

/** Route `handle` of the route that matches unknown URLs. */
export const NOT_FOUND_ROUTE = { notFound: true } as const;

function isNotFoundRoute(handle: unknown): boolean {
  return (
    typeof handle === 'object' &&
    handle !== null &&
    (handle as { notFound?: unknown }).notFound === true
  );
}

/**
 * Renders children for signed-in users. Everyone else is sent to /login with the current path as `next`, except
 * on an unknown URL, which shows the standalone not-found page. When the session cannot be checked, a standalone
 * page reports it with Try again; it stays rendered while the retry runs.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  const unknownUrl = useMatches().some((match) => isNotFoundRoute(match.handle));
  const loadError = useLoadError(session);
  const error = session.isError ? session.error : loadError;
  if (error) {
    if (isApiError(error) && error.status === 401) {
      if (unknownUrl) return <NotFoundPage />;
      const next = `${location.pathname}${location.search}`;
      return (
        <Navigate
          to={next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`}
          replace
        />
      );
    }
    return (
      <StandaloneLayout>
        <PageContainer>
          <h1 className="sr-only">ComfiConf is not reachable right now</h1>
          <LoadFailedNotice
            className="max-w-2xl"
            title="ComfiConf is not reachable right now"
            error={error}
            errorKey={session.errorUpdatedAt}
            retrying={session.isFetching}
            onRetry={() => void session.refetch()}
          />
        </PageContainer>
      </StandaloneLayout>
    );
  }
  if (session.isPending) return <FullPageLoader />;
  return <>{children}</>;
}

/** Login and registration: signed-in users continue to `next` (or the dashboard). */
export function PublicOnly({ children }: { children: ReactNode }) {
  const session = useSession();
  const [params] = useSearchParams();
  if (session.isPending) return <FullPageLoader />;
  if (session.data) return <Navigate to={safeNextPath(params.get('next')) ?? '/'} replace />;
  return <>{children}</>;
}
